// src/components/Capabilities.js
// Animation timing: cardScrollRange=0.14, section=450vh, pill stays until 88-94%

import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, useScroll, useTransform, useSpring, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Brain, BarChart3, Code2, Sparkles, Calendar, FileText, Wrench, Zap, Activity, Search, Settings, AlertCircle, Layout, Cloud, Shield, Layers, X } from 'lucide-react';
import { pillarsData } from '../data/capabilitiesData';
import './Capabilities.css';

// Icon mapping
const iconMap = { Brain, BarChart3, Code2 };

// Bullet icon mapping for each pillar
const bulletIconMap = { Calendar, FileText, Wrench, Zap, Activity, Search, Settings, AlertCircle, Layout, Cloud, Shield, Layers };

// Stacking Card Component - Cards stack ON TOP with visible edges of cards behind
const StackingCard = ({ pillar, index, scrollYProgress, totalCards }) => {
  const IconComponent = iconMap[pillar.icon];
  const prefersReducedMotion = useReducedMotion();

  // Modal state for fullscreen image view
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleImageClick = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  // Close modal on ESC key and prevent body scroll when open
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    if (isModalOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  // Cards stack with settling gaps (scaled for 450vh section)
  const cardScrollRange = 0.14;
  const cardStart = 0.43 + (index * cardScrollRange);
  const cardPeak = cardStart + 0.07;

  // Card entrance animation (slide up from below)
  const stackOffset = 10;
  const y = useTransform(
    scrollYProgress,
    [cardStart, cardPeak],
    [200, index * stackOffset]
  );

  // Card opacity (fade in)
  const opacity = useTransform(
    scrollYProgress,
    [cardStart, cardStart + 0.05],
    [0, 1]
  );

  // Smooth spring animations
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });
  const smoothOpacity = useSpring(opacity, { stiffness: 100, damping: 30 });

  return (
    <>
      <motion.div
        className="stacking-card"
        style={{
          y: smoothY,
          opacity: smoothOpacity,
          zIndex: index + 1,
        }}
      >
        <div className="stacking-card__inner">
          {/* ZONE A - Top Meta Row */}
          <div className="stacking-card__header">
            <div className="stacking-card__label">
              <IconComponent size={16} />
              <span>{pillar.label}</span>
            </div>
            <h3 className="stacking-card__title">{pillar.title}</h3>
          </div>

          {/* ZONE B - Hero Image - Click to expand */}
          <div className="stacking-card__image-zone">
            <div
              className="stacking-card__image-frame"
              onClick={handleImageClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleImageClick()}
            >
              <div className="stacking-card__image-inner">
                <img
                  src={pillar.image}
                  alt={pillar.title}
                  className="stacking-card__image"
                  loading="lazy"
                  width={1100}
                  height={614}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.stacking-card__image-placeholder');
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
                {/* Placeholder */}
                <div className="stacking-card__image-placeholder">
                  <IconComponent size={48} />
                  <span>Image Coming Soon</span>
                </div>
              </div>
            </div>
          </div>

          {/* ZONE C - Details & Tags */}
          <div className="stacking-card__details">
            <ul className="stacking-card__bullets">
              {pillar.bullets.map((bullet, i) => {
                const BulletIcon = bulletIconMap[pillar.bulletIcons?.[i]] || Sparkles;
                return (
                  <li key={i}>
                    <BulletIcon size={14} className="bullet-icon" />
                    {bullet}
                  </li>
                );
              })}
            </ul>
            <div className="stacking-card__tags">
              {pillar.tags.map((tag, i) => (
                <span key={i} className="stacking-card__tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Image Modal - Rendered via Portal to break out of card container */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              className="image-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={handleModalClose}
            >
              {/* Animated gradient glow behind image */}
              <div className="image-modal__glow" />

              {/* Close button */}
              <button
                className="image-modal__close"
                onClick={handleModalClose}
                aria-label="Close modal"
              >
                <X size={24} />
              </button>

              {/* Image container with spring animation */}
              <motion.div
                className="image-modal__content"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { type: "spring", damping: 25, stiffness: 300 }
                }
                onClick={(e) => e.stopPropagation()}
              >
                <img src={pillar.image} alt={pillar.title} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

const Capabilities = () => {
  const sectionRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Note: useInView was previously used for pill visibility but now controlled by scrollYProgress

  // Calculate translateX based on viewport width
  const [translateXDistance, setTranslateXDistance] = useState(-450);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Calculate distance to move from center to left edge
      setTranslateXDistance(-(width * 0.34));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll progress for the entire section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  // ============================================
  // PHASE A-B (0-15%): Title fades in at CENTER
  // No Y movement needed - CSS flex centers content naturally
  // ============================================

  // Header container opacity (scaled for 450vh section)
  const headerOpacity = useTransform(
    scrollYProgress,
    [0.12, 0.19],
    [0, 1]
  );

  // Individual line opacities (staggered fade-in, scaled for 450vh)
  const titleLine1Opacity = useTransform(
    scrollYProgress,
    [0.16, 0.23],
    [0, 1]
  );

  const titleLine2Opacity = useTransform(
    scrollYProgress,
    [0.19, 0.27],
    [0, 1]
  );

  const subtitleOpacity = useTransform(
    scrollYProgress,
    [0.23, 0.31],
    [0, 1]
  );

  // ============================================
  // PHASE C-D (30-45%): Title moves LEFT via X transform
  // ============================================

  // Title moves left via translateX (from center to left edge, scaled for 450vh)
  const titleTranslateX = useTransform(
    scrollYProgress,
    [0.35, 0.47],
    [0, translateXDistance]
  );

  // Right column opacity (appears as title moves left, scaled for 450vh)
  const rightColumnOpacity = useTransform(
    scrollYProgress,
    [0.39, 0.47],
    [0, 1]
  );

  // Pill opacity - fade in when section is visible, stays until section exits
  const pillOpacity = useTransform(
    scrollYProgress,
    [0.10, 0.17, 0.88, 0.94],
    [0, 1, 1, 0]
  );

  // ============================================
  // Smooth springs - HIGH DAMPING = NO OVERSHOOT
  // ============================================
  const smoothHeaderOpacity = useSpring(headerOpacity, { stiffness: 100, damping: 40 });
  const smoothTitleLine1Opacity = useSpring(titleLine1Opacity, { stiffness: 100, damping: 40 });
  const smoothTitleLine2Opacity = useSpring(titleLine2Opacity, { stiffness: 100, damping: 40 });
  const smoothSubtitleOpacity = useSpring(subtitleOpacity, { stiffness: 100, damping: 40 });
  const smoothTitleTranslateX = useSpring(titleTranslateX, { stiffness: 60, damping: 25 });
  const smoothRightColumnOpacity = useSpring(rightColumnOpacity, { stiffness: 60, damping: 25 });
  const smoothPillOpacity = useSpring(pillOpacity, { stiffness: 100, damping: 30 });

  // For reduced motion ONLY, render a simplified static version
  if (prefersReducedMotion) {
    return (
      <section id="capabilities" className="capabilities-section capabilities-section--reduced">
        {/* Section Pill */}
        <div className="capabilities-section-pill">
          <Sparkles size={14} />
          <span>Our Capabilities</span>
        </div>

        <div className="capabilities-container">
          <div className="capabilities-header">
            <h2 className="capabilities-title capabilities-title--hero">
              Three Pillars of <span className="gradient-text">Operational Excellence</span>
            </h2>
            <p className="capabilities-subtitle">
              We combine cutting-edge AI technology, advanced analytics, and custom software
              development to transform how enterprises operate.
            </p>
          </div>

          <div className="capabilities-cards-static">
            {pillarsData.map((pillar, index) => {
              const IconComponent = iconMap[pillar.icon];
              return (
                <div
                  key={pillar.id}
                  className="static-card"
                >
                  <div className="static-card__label">
                    <IconComponent size={16} />
                    <span>{pillar.label}</span>
                  </div>
                  <h3>{pillar.title}</h3>
                  <ul>
                    {pillar.bullets.map((bullet, i) => (
                      <li key={i}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="capabilities"
      className="capabilities-section"
    >
      {/* Background Effects */}
      <div className="capabilities-bg">
        <div className="capabilities-bg__gradient" />
        <div className="capabilities-bg__blob capabilities-bg__blob--1" />
        <div className="capabilities-bg__blob capabilities-bg__blob--2" />
        <div className="capabilities-bg__grid" />
      </div>

      {/* "Our Capabilities" Pill - Fades in/out with section */}
      <motion.div
        className="capabilities-section-pill"
        style={{ opacity: smoothPillOpacity }}
      >
        <Sparkles size={14} />
        <span>Our Capabilities</span>
      </motion.div>

      {/* Sticky Container */}
      <div className="capabilities-sticky">
        <div className="capabilities-layout">

          {/* Left Column - Centered via position + transform */}
          <motion.div
            className="capabilities-left"
            style={{
              opacity: smoothHeaderOpacity,
              x: smoothTitleTranslateX,
              y: "-50%", // Static offset to center vertically (works with top: 50%)
            }}
          >
            {/* Text Content */}
            <div className="capabilities-left__content">
              {/* Title - Row by row appearance */}
              <h2 className="capabilities-title capabilities-title--hero">
                {/* Line 1: "Three Pillars of" */}
                <motion.span
                  style={{
                    display: 'block',
                    opacity: smoothTitleLine1Opacity,
                  }}
                >
                  Three Pillars of
                </motion.span>
                {/* Line 2: "Operational" */}
                <motion.span
                  className="gradient-text"
                  style={{
                    display: 'block',
                    opacity: smoothTitleLine2Opacity,
                  }}
                >
                  Operational
                </motion.span>
                {/* Line 3: "Excellence" */}
                <motion.span
                  className="gradient-text"
                  style={{
                    display: 'block',
                    opacity: smoothTitleLine2Opacity,
                  }}
                >
                  Excellence
                </motion.span>
              </h2>

              {/* Subtitle - appears after title lines */}
              <motion.p
                className="capabilities-subtitle"
                style={{
                  opacity: smoothSubtitleOpacity,
                }}
              >
                We combine cutting-edge AI technology, advanced analytics, and custom software
                development to transform how enterprises operate.
              </motion.p>
            </div>
          </motion.div>

          {/* Right Column - Stacking Cards */}
          <motion.div
            className="capabilities-right"
            style={{ opacity: smoothRightColumnOpacity }}
          >
            <div className="capabilities-cards-stack">
              {pillarsData.map((pillar, index) => (
                <StackingCard
                  key={pillar.id}
                  pillar={pillar}
                  index={index}
                  scrollYProgress={scrollYProgress}
                  totalCards={pillarsData.length}
                />
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Capabilities;
